/**
 * Type-checked merging of untrusted settings (session files) into current
 * settings: a field is accepted only when its type matches the default and,
 * for enumerated fields, its value is one of the allowed options.
 */
import { COLORMAPS } from '../core/colormaps';
import { PRIMARIES, TRANSFER_LABELS } from '../core/color';
import { QUANTITIES } from '../core/pipeline';
import { SPATIAL_UNITS } from '../core/units';

type Obj = Record<string, unknown>;

/** Allowed values of enumerated fields, by field name (names are unique across settings). */
export const ENUMS: Record<string, readonly string[]> = {
  transferMode: ['auto', 'manual'],
  primariesMode: ['auto', 'manual'],
  transfer: Object.keys(TRANSFER_LABELS),
  primaries: [...Object.keys(PRIMARIES), 'custom'],
  darkMode: ['none', 'constant', 'frame'],
  clipDetection: ['auto', 'on', 'off'],
  interp: ['nearest', 'bilinear', 'bicubic'],
  stat: ['mean', 'median', 'min', 'max', 'sum'],
  oob: ['nan', 'clamp'],
  mode: ['color', 'gray', 'false', 'none', 'scale', 'twoPoint', 'poly', 'lut'],
  colormap: COLORMAPS.map((c) => c.id),
  unit: [...SPATIAL_UNITS.map((u) => u.id)],
  baseline: ['zero', 'min', 'edges', 'edgesLinear', 'percentile', 'manual'],
  polarity: ['peak', 'dip'],
  peakMode: ['highest', 'all'],
  window: ['none', 'hann', 'hamming', 'blackman'],
  detrend: ['none', 'mean', 'linear'],
  envelope: ['none', 'sd', 'minmax'],
  whiteBalance: ['camera', 'auto', 'unity'],
};

/** Fields whose enum depends on the parent object (e.g. `unit` is free text in the calibration). */
const FREE_TEXT = new Set(['calibration.unit']);

export function mergeChecked<T extends object>(current: T, incoming: unknown, path = ''): T {
  if (!incoming || typeof incoming !== 'object' || Array.isArray(incoming)) return current;
  const inc = incoming as Obj;
  const out: Obj = { ...(current as Obj) };
  for (const [k, def] of Object.entries(current)) {
    if (!(k in inc)) continue;
    const v = inc[k];
    const key = path ? `${path}.${k}` : k;
    if (typeof def === 'number') {
      if (typeof v === 'number' && !Number.isNaN(v)) out[k] = v;
    } else if (typeof def === 'string') {
      if (typeof v === 'string' && (FREE_TEXT.has(key) || !ENUMS[k] || ENUMS[k].includes(v))) out[k] = v;
    } else if (typeof def === 'boolean') {
      if (typeof v === 'boolean') out[k] = v;
    } else if (Array.isArray(def)) {
      if (Array.isArray(v)) out[k] = v;
    } else if (def === null) {
      if (v === null || typeof v === 'number') out[k] = v;
    } else if (typeof def === 'object') {
      out[k] = mergeChecked(def as object, v, key);
    }
  }
  return out as T;
}

export function validQuantity(q: unknown): q is (typeof QUANTITIES)[number]['id'] {
  return typeof q === 'string' && QUANTITIES.some((x) => x.id === q);
}
